import { heapEvent } from "@equipmentshare/heaper";
import { Runtime } from "fp";
import { useQueryClient } from "react-query";

import { Remote } from "~/Remote";
import { Time } from "~/Time";
import { User } from "../User";
import * as Request from "./Request";

export { Request };

export type PreferenceKeyBase =
  | "dateSpan"
  | "locationFilter"
  | "branchFilter"
  | "assetFilter";

export type PreferenceKeyContext = "driversTable" | "logsTable" | "dvirTable";

export type UpdateSourceNames =
  | "DVIR Table"
  | "Drivers Table"
  | "Logs Table"
  | "Unassigned Logs Table";

export const preferenceKey = (
  base: PreferenceKeyBase,
  context?: PreferenceKeyContext
) => {
  return `web.${base}${context ? "." + context : ""}`;
};

export type UserPreferences<T extends unknown> = Record<string, T>;

export namespace Get {
  export const use = <T>(): Remote<UserPreferences<T>> => {
    const user = User.use();
    return Remote.Query.use(
      [Request.Query.tag(), user.id],
      Request.Query.Get.request(user),
      {
        keepPreviousData: true,
        staleTime: Time.Duration.minutes(5).duration,
      }
    );
  };

  export const useLoading = (): readonly [
        boolean,
        Remote<UserPreferences<unknown>>
      ] => {
        const [loading, preferences] = Remote.when(
          Get.use(),
          {
            loading: () => [true, {} as Remote<UserPreferences<unknown>>],
            error: () => [false, {} as Remote<UserPreferences<unknown>>],
            success: (preferences) => [false, preferences as Remote<UserPreferences<unknown>>],
          }
        );
        return [loading, preferences ];
      };

  export const usePrefetch = () => {
    const user = User.use();
    Remote.toUndefined(
      Remote.Query.use(
        [Request.Query.tag(), user.id],
        Request.Query.Get.request(user),
        {
          staleTime: Time.Duration.minutes(1).duration,
        }
      )
    );
  };
}

export namespace Put {
  export const track = <T>(
    preferences: UserPreferences<T>,
    sourceName: UpdateSourceNames,
    filterType: PreferenceKeyBase
  ) => {
    if (filterType === "locationFilter") {
      const locations = preferences[preferenceKey("locationFilter")];

      heapEvent("E-Logs - Settings - Click - WRL Filter Change", {
        work_reporting_locations: String(locations),
        location_count: Array.isArray(locations) ? locations.length : -1,
        page_source: sourceName,
      });
    } else if (filterType === "branchFilter") {
      const branches = preferences[preferenceKey("branchFilter")];
      heapEvent("E-Logs - Settings - Click - Branch Filter Change", {
        branches: String(branches),
        branch_count: Array.isArray(branches) ? branches.length : -1,
        page_source: sourceName,
      });
    } else if (filterType === "assetFilter") {
      const assets = preferences[preferenceKey("assetFilter")];
      heapEvent("E-Logs - Settings - Click - Asset Filter Change", {
        assets: String(assets),
        asset_count: Array.isArray(assets) ? assets.length : -1,
        page_source: sourceName,
      });
    }
  };

  export const use = <T>() => {
    const user = User.use();
    const queryClient = useQueryClient();

    return Remote.Mutation.use(
      (
        preferences: UserPreferences<T>,
        sourceName: UpdateSourceNames,
        filterType: PreferenceKeyBase
      ) => {
        track(preferences, sourceName, filterType);

        return Request.Query.Put.request({
          id: user.id,
          preferences,
        });
      },
      {
        onSuccess: () => {
          return queryClient.invalidateQueries([Request.Query.tag()]);
        },
      }
    );
  };
}

export namespace Update {
  export const use = () => {
    const user = User.use();
    const client = useQueryClient();
    return (preferences: UserPreferences<unknown>) => {
      client.setQueryData([Request.Query.tag(), user.id], preferences);
    };
  };
}

export namespace Invalidate {
  export const use = () => {
    const user = User.use();
    const client = useQueryClient();
    return () =>
      client.invalidateQueries(Request.Query.key(user), {
        exact: false,
        refetchActive: true,
        refetchInactive: true,
      });
  };
}

export namespace Response {
  export const runtime = () => {
    return Runtime.strict({
      preferences: Runtime.unknownRecord(),
    });
  };

  type DecodableResponse = Runtime.ToType<typeof runtime>;

  export const decode = <T extends unknown>(
    response: DecodableResponse
  ): Record<string, T> => {
    return response.preferences as never;
  };
}
